import React from 'react';
import { Box } from '@mui/material';
import { Button } from '@mui/joy';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useKeycloak } from '@react-keycloak/web';
import { getUserResources } from '@dreebit-registry/vsm-microservice-components';
import { BoltRounded, FactCheckRounded, TableChartViewRounded } from '@mui-symbols-material/w300';

import AssetLocationImportWidget from '../AssetLocationImportWidget';
import AssetEventHistoryWidget from '../AssetEventHistoryWidget';
import AssetServiceReminderWidget from '../AssetServiceReminderWidget';
import AssetInstallationsWidget from '../AssetInstallationsWidget';
import AssetEventActionShortcuts from '../AssetEventActionShortcuts';
import IoTConnectionAdmin from '../IoTConnectionAdmin';
import IotCommissioningWizardModal from '../IotCommissioningWizard/IotCommissioningWizardModal';

import HeroLead, { resolveHeroLeadState } from '../dashboardHero/HeroLead';
import HeroCustomerCard from '../dashboardHero/HeroCustomerCard';
import HeroServiceUnitCard from '../dashboardHero/HeroServiceUnitCard';
import SubAssetsListWidget from '../dashboardHero/SubAssetsListWidget';
import FilesListWidget from '../dashboardHero/FilesListWidget';
import HeroRmaAction from '../dashboardHero/HeroRmaAction';
import { HeroBanner, HeroKpi, WarrantyChip, FeedGrid, FeedColumn } from '../dashboardHero/heroPrimitives';
import { HeroActionsSkeleton, HeroLeadSkeleton, fadeInSx } from '../dashboardHero/HeroSkeletons';

import { AssetDashboardHero } from '../../graphql/queries/assetDashboardHero';
import { AssetSubAssetCount } from '../../graphql/queries/assetSubAssetCount';
import { AssetFilesTotal } from '../../graphql/queries/assetFilesTotal';
import { InstalledBaseEventList } from '../../graphql/queries/installedBaseEventList';
import { AssetIoTHubStatus, AssetNetworkInfo } from '../../graphql/queries/assetIoTConnectionWidget';
import { AssetIoTFirmware } from '../../graphql/queries/assetIoTFirmware';
import { AssetIndexIotSnapshot } from '../../graphql/queries/assetIndexIotSnapshot';
import { AssetAssetLocation } from '../../graphql/queries/assetAssetLocation';
import { InstalledBaseNodeEventContext } from '../../graphql/queries/installedBaseNodeEventContext';
import { AssetNameplateBasicInfo } from '../../graphql/queries/assetNameplateBasicInfo';
import { isContractValid, isContractExpired } from '../../utils/isContractValid';
import { isIotAttachEntryVisible } from '../../utils/iotAttachGate';
import { resolveIotCommissioningState } from '../../utils/iotCommissioningState';
import { isSlaEnabled } from '../../utils/slaEnabled';
import { withVisibleEventCategories } from '../../utils/eventListFilter';
import { useAssetSlaStatus } from '../../hooks/useAssetSlaStatus';
import { readDevSlaOverride } from '../../utils/devSlaOverride';
import { getClosestPathNodeByKey } from '../../utils/getClosestPathNodeTitle';
import { IndustryType } from '../../hooks/useIndustryType';
import { InstalledBaseNodeTypeKey } from '../../codegen/graphql';

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
    hasIotConnection: boolean;
    isPendingIotConnection: boolean;
    isIoTAdmin: boolean;
    hasServiceReminders: boolean;
    serialNumber?: string;
    compact?: boolean;
}

const AssetDashboard = ({
    assetLocationId,
    installedBaseNodeId,
    hasIotConnection,
    isPendingIotConnection,
    isIoTAdmin,
    hasServiceReminders,
    serialNumber,
    compact,
}: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const administrateCustomers = getUserResources(keycloak.token, 'otto_crm').includes('administrateCustomers');

    // One round trip decides the hero lead state (contract + connection status).
    // Skeleton only on first load — revisits paint from cache and refresh silently.
    const heroQuery = useQuery(AssetDashboardHero, { variables: { id: assetLocationId }, fetchPolicy: 'cache-and-network' });
    const heroPending = heroQuery.loading && !heroQuery.data;
    const contractData = _.get(heroQuery, 'data.assetLocation.owner.contract');
    const clientId = _.get(heroQuery, 'data.assetLocation.owner.id');
    const industryType = _.get(heroQuery, 'data.assetLocation.owner.industryType');
    const isSemiconductor = industryType === IndustryType.SEMICONDUCTOR;
    const warrantyEndDate = _.get(heroQuery, 'data.assetLocation.warrantyEndDate');
    const assetId = _.get(heroQuery, 'data.assetLocation.asset.id');
    const runtimeHours = _.get(heroQuery, 'data.assetLocation.runtimeHours');
    const averageRuntimePerDay = _.get(heroQuery, 'data.assetLocation.averageRuntimePerDay');
    // Tool > Chamber location path for the Customer widget (closest ancestors of each type).
    const locationPath = isSemiconductor
        ? [InstalledBaseNodeTypeKey.SEMICONDUCTOR_TOOL, InstalledBaseNodeTypeKey.SEMICONDUCTOR_CHAMBER]
              .map((key) => getClosestPathNodeByKey(_.get(heroQuery, 'data'), key))
              .filter((node): node is NonNullable<typeof node> => node !== null)
        : undefined;

    // Device presence is the union of the hero query and the route's id-only query
    // (hasIotConnection prop): the union can only add detection, so the lead never
    // shows a "no device" state for a live device if either source knows better —
    // the heroPending gate still prevents any flash before the hero settles.
    // Dev-only: the `?slaStatus=…` override (see devSlaOverride.ts) also presents the
    // asset as connected, so the SLA chip and the commissioning can be tried on
    // testing, where no industry asset carries an IoT connection with an SLA record.
    const { search } = useLocation();
    const slaOverride = !!readDevSlaOverride(search);

    // First paint from the search index (IBM-1053): one cheap request without an IoT
    // subgraph hop. Every value below is `live ?? index` — the live entity queries keep
    // running and win once they answer, because the index can lag behind.
    const indexQuery = useQuery(AssetIndexIotSnapshot, {
        variables: { id: assetLocationId, withSla: isSlaEnabled() },
        fetchPolicy: 'cache-and-network',
    });
    const indexAttributes = _.get(indexQuery, 'data.searchAssets.searchAssetResults[0].attributes');

    const iotConnected =
        !!_.get(heroQuery, 'data.assetLocation.iotConnection.id') ||
        hasIotConnection ||
        !!indexAttributes?.iotConnectionStatus ||
        slaOverride;

    const contractValid = isContractValid(contractData);
    const contractExpired = isContractExpired(contractData);
    const state = resolveHeroLeadState({ contractValid, contractExpired, hasIotConnection: iotConnected });

    const subAssetCountQuery = useQuery(AssetSubAssetCount, { variables: { id: assetLocationId } });
    const subAssetCount = _.get(subAssetCountQuery, 'data.assetLocation.installedBaseNode.assetCount', 0) ?? 0;

    const filesTotalQuery = useQuery(AssetFilesTotal, { variables: { id: assetLocationId } });
    const filesTotal =
        (_.get(filesTotalQuery, 'data.assetLocation.asset.product.fileList.total', 0) ?? 0) +
        (_.get(filesTotalQuery, 'data.assetLocation.installedBaseNode.fileList.total', 0) ?? 0);

    const eventCountQuery = useQuery(InstalledBaseEventList, {
        variables: {
            filter: withVisibleEventCategories({ assetId: { equals: assetId }, installedBaseNodeId: { equals: installedBaseNodeId } }),
            pagination: { start: 0, limit: 1 },
            sort: { insert: 'desc' },
        },
        skip: !assetId,
    });
    const eventTotal = _.get(eventCountQuery, 'data.installedBaseEventList.total', 0) ?? 0;

    // Live connection status ships with the hero query; only the slow network
    // information stays a separate request so it can stream in after the lead.
    // The skip deliberately uses the prop: it can settle before the hero query,
    // starting the slowest request as early as possible.
    const showLive = iotConnected && !isSemiconductor;
    // no-cache: see AssetNetworkInfo — a failed lookup nulls the whole iotConnection.
    const networkQuery = useQuery(AssetNetworkInfo, { variables: { id: assetLocationId }, skip: !showLive, fetchPolicy: 'no-cache' });
    const connectionStatus =
        _.get(heroQuery, 'data.assetLocation.iotConnection.connectionStatus') ?? indexAttributes?.iotConnectionStatus ?? undefined;
    // Hub connectivity streams in via its own tolerant request: the backend nulls
    // the whole iotConnection when the hub lookup fails, so the field can't join
    // the hero query, and no-cache keeps that null from clobbering the cached
    // connection (see AssetIoTHubStatus).
    const hubQuery = useQuery(AssetIoTHubStatus, {
        variables: { id: assetLocationId },
        skip: !showLive,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });
    // `??`: a failed live lookup (nulled iotConnection) keeps the index value.
    const hubStatus =
        _.get(hubQuery, 'data.assetLocation.iotConnection.iotHubConnectivityStatus') ?? indexAttributes?.iotHubConnectivityStatus;
    const hubOnline = hubStatus === 'CONNECTED';
    const networkStandard = _.get(networkQuery, 'data.assetLocation.iotConnection.networkInformation.currentStandard');
    const networkProvider = _.get(networkQuery, 'data.assetLocation.iotConnection.networkInformation.networkProvider');
    const signalStrength = _.get(networkQuery, 'data.assetLocation.iotConnection.networkInformation.signalStrength');
    const networkInfoLoading = showLive && networkQuery.loading && !networkQuery.data;

    // Connection and modem/gateway firmware for the lead's firmware line. The resolver
    // is cheap, but the fields can't join the cached hero/network queries: the backend
    // returns gateway objects with an empty id when firmwareVersion is selected, which
    // breaks cache normalization — hence the dedicated no-cache request.
    const firmwareQuery = useQuery(AssetIoTFirmware, {
        variables: { id: assetLocationId },
        skip: !showLive,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });
    const firmwareVersion =
        _.get(firmwareQuery, 'data.assetLocation.iotConnection.firmwareVersion') ?? indexAttributes?.firmwareVersion;
    const modemFirmwareVersion =
        _.get(firmwareQuery, 'data.assetLocation.iotConnection.gateway.firmwareVersion') ?? indexAttributes?.modemFirmwareVersion;
    const firmwareLoading = showLive && firmwareQuery.loading && !firmwareQuery.data && !firmwareVersion && !modemFirmwareVersion;

    // SLA status + commissioning (IBM-1045): IoT admins only — the chip in the
    // lead and the "Start commissioning" CTA whenever the SLA is INACTIVE / EVALUATION_FAILED, also while
    // a commissioning submitted in the last 24 h is pending (the lead then shows the
    // "submitted" line next to it, so the checklist can be repeated on site).
    const sla = useAssetSlaStatus({
        assetLocationId,
        assetId,
        enabled: showLive && isIoTAdmin,
        indexFallback: indexAttributes
            ? {
                  status: indexAttributes.iotSlaStatus ?? null,
                  level: indexAttributes.iotSlaLevel ?? null,
                  connectivityQuality: indexAttributes.iotConnectivityQuality ?? null,
              }
            : undefined,
    });
    const commissioningState = resolveIotCommissioningState({
        enabled: isSlaEnabled(),
        isIoTAdmin,
        hasConnection: iotConnected,
        slaStatus: sla.status,
        lastCommissioningAt: sla.lastCommissioningAt,
        lookupSettled: sla.commissioningLookupSettled,
    });
    // The SLA line waits for the commissioning lookup like the lead waits for the hero
    // query: "Commissioning required" must not flash before the pending line.
    const slaShown = sla.status && sla.commissioningLookupSettled ? { status: sla.status, level: sla.level } : null;
    const [commissioningOpen, setCommissioningOpen] = React.useState(false);

    // The actions row is composed from independently loading sources — the event
    // shortcuts (AssetAssetLocation + InstalledBaseNodeEventContext), the RMA link
    // (AssetNameplateBasicInfo), the SLA / commissioning lookups and the hero query.
    // Rendering it piecewise makes buttons pop in one after another, so the row (and
    // the lead, whose SLA line depends on the same lookups) stays a skeleton until
    // every source has answered. The same documents are asked here and in the child
    // components: Apollo deduplicates the requests in flight, and the children then
    // read the cached answer synchronously when the row is finally mounted.
    const settles = (query: { data?: unknown; loading: boolean; called?: boolean }) => !!query.data || !query.loading;
    const shortcutsAssetQuery = useQuery(AssetAssetLocation, { variables: { id: assetLocationId } });
    const eventContextQuery = useQuery(InstalledBaseNodeEventContext, { variables: { id: installedBaseNodeId }, skip: !installedBaseNodeId });
    const rmaQuery = useQuery(AssetNameplateBasicInfo, { variables: { id: assetLocationId } });
    const heroSettled = !heroPending && sla.settled && settles(shortcutsAssetQuery) && settles(eventContextQuery) && settles(rmaQuery);

    // Admin CTAs: add a contract (none yet — also for already-connected devices) or
    // extend an expired one. Industry only, and only with an owner client — an
    // asset in a User Pool has none, so there is no customer to attach a contract to.
    // All state-dependent chrome is held back until the hero query settles so nothing flashes.
    const showAddContractBar = !heroPending && !!clientId && !isSemiconductor && administrateCustomers && isIoTAdmin && !contractValid && !contractExpired && !isPendingIotConnection;
    const showExtendContractBar = !heroPending && !!clientId && !isSemiconductor && administrateCustomers && isIoTAdmin && contractExpired;
    // Inline "Connect device" — same frontend rule as the connect route guard
    // (src/utils/iotAttachGate.ts); a blocked network scan is explained by the wizard.
    const showConnect = !heroPending && isIotAttachEntryVisible({
        isIoTAdmin, isInstalled: !!installedBaseNodeId, hasIotConnection: iotConnected,
        isPendingIotConnection, contractValid, isSemiconductor,
    });
    const showTelemetry = !isSemiconductor && iotConnected;
    const showReminders = !heroPending && (isSemiconductor || !iotConnected || hasServiceReminders);

    // The lead is gated on the hero query — a wrong state (e.g. "Not connected"
    // flashing before the connection status arrives) is worse than a skeleton.
    const lead = heroPending || !sla.settled ? (
        <HeroLeadSkeleton compact={compact} withMetaLine={showLive} />
    ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, ...fadeInSx }}>
            <HeroLead
                industryType={industryType}
                runtimeHours={runtimeHours}
                averageRuntimePerDay={averageRuntimePerDay}
                state={state}
                isAdmin={isIoTAdmin}
                hasIotConnection={iotConnected}
                compact={compact}
                connectionStatus={connectionStatus}
                hubOnline={hubOnline}
                networkStandard={networkStandard}
                networkProvider={networkProvider}
                signalStrength={signalStrength}
                networkInfoLoading={networkInfoLoading}
                firmwareVersion={firmwareVersion}
                modemFirmwareVersion={modemFirmwareVersion}
                firmwareLoading={firmwareLoading}
                sla={slaShown}
                commissioningPendingAt={commissioningState === 'pending' ? sla.lastCommissioningAt : null}
                // Keeps the dev-only `?slaStatus=…` override, so the report route sees the same status.
                onSlaClick={() => navigate(`/assets/${assetLocationId}/conditionMonitoring/slaReport${slaOverride ? search : ''}`)}
            />
        </Box>
    );

    const kpis = (
        <>
            <HeroKpi value={subAssetCount} label={t('Sub-assets')} loading={subAssetCountQuery.loading && !subAssetCountQuery.data} />
            <HeroKpi value={filesTotal} label={t('Documents')} loading={filesTotalQuery.loading && !filesTotalQuery.data} />
            <HeroKpi value={eventTotal} label={t('Events')} loading={heroPending || (eventCountQuery.loading && !eventCountQuery.data)} />
            <WarrantyChip warrantyEndDate={warrantyEndDate} />
        </>
    );

    const actions = !heroSettled ? (
        <HeroActionsSkeleton />
    ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', flex: 1, ...fadeInSx }}>
            {showConnect && (
                <Button
                    variant="solid"
                    startDecorator={<BoltRounded sx={{ fontSize: 20 }} />}
                    onClick={() => navigate(`/assets/${assetLocationId}/connect`)}
                    sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.700' } }}
                >
                    {t('Connect device')}
                </Button>
            )}
            {commissioningState !== 'none' && (
                <Button
                    variant="solid"
                    startDecorator={<FactCheckRounded sx={{ fontSize: 20 }} />}
                    onClick={() => setCommissioningOpen(true)}
                    sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.700' } }}
                    data-testid="start-commissioning"
                >
                    {t('Start commissioning')}
                </Button>
            )}
            <AssetEventActionShortcuts assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} />
            <HeroRmaAction assetLocationId={assetLocationId} />
            {showTelemetry && (
                <Button
                    variant="plain"
                    startDecorator={<TableChartViewRounded sx={{ fontSize: 20 }} />}
                    onClick={() => navigate(`/assets/${assetLocationId}/conditionMonitoring/telemetry`)}
                    sx={{ color: 'primary.500', ml: { md: 'auto' }, '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' } }}
                >
                    {t('Open telemetry & charts')}
                </Button>
            )}
        </Box>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: '16px' }}>
                <AssetLocationImportWidget assetLocationId={assetLocationId} />
            </Box>
            {showAddContractBar && (
                <Box sx={{ mb: '16px' }}>
                    <IoTConnectionAdmin clientId={clientId} assetLocationId={assetLocationId} variant="add" />
                </Box>
            )}
            {showExtendContractBar && (
                <Box sx={{ mb: '16px' }}>
                    <IoTConnectionAdmin clientId={clientId} assetLocationId={assetLocationId} variant="extend" />
                </Box>
            )}

            <HeroBanner lead={lead} kpis={kpis} actions={actions} />
            {commissioningOpen && assetId && (
                <IotCommissioningWizardModal
                    assetId={assetId}
                    assetLocationId={assetLocationId}
                    installedBaseNodeId={installedBaseNodeId}
                    onClose={() => setCommissioningOpen(false)}
                    onSuccess={sla.refetch}
                />
            )}

            <FeedGrid compact={compact}>
                <FeedColumn>
                    <AssetEventHistoryWidget assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} compact={compact} />
                    {showReminders && (
                        <AssetServiceReminderWidget assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} compact={compact} />
                    )}
                    {serialNumber && <AssetInstallationsWidget serialNumber={serialNumber} assetLocationId={assetLocationId} compact={compact} />}
                </FeedColumn>
                <FeedColumn>
                    <HeroCustomerCard variant="asset" id={assetLocationId} locationPath={locationPath} nodeId={installedBaseNodeId} />
                    <HeroServiceUnitCard nodeId={installedBaseNodeId} />
                    <SubAssetsListWidget installedBaseNodeId={installedBaseNodeId} compact={compact} />
                    <FilesListWidget variant="asset" id={assetLocationId} compact={compact} />
                </FeedColumn>
            </FeedGrid>
        </Box>
    );
};

export default AssetDashboard;
